import cn from 'classnames';
import React, { forwardRef, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, To } from 'react-router-dom';
import { UseFormReturn, SubmitHandler } from 'react-hook-form';
import { Divider } from '@react-md/divider';
import { TextField, Checkbox } from '@react-md/form';
import { FormField, NonFieldErrors, NON_FIELD_ERROR_KEY } from '@components/form-field';
import { FeatureContent } from '@components/feature-content';
import { SubmitButton } from '@components/submit-button';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { CreateUserRequest, useClientsCreateMutation } from '@services/df/clients';
import { processEmailError } from '@modules/auth';
import { getNodeIdComposer, setApiErrors } from '@utils';
import { useFormLabels } from './hooks';
import { FormId, RootPrefix } from './constants';

import styles from './client-form.module.scss';

const _id = getNodeIdComposer(RootPrefix, 'create_client_tab');

interface ClientFormProps extends HTMLAttributes<HTMLDivElement> {
    form: UseFormReturn<CreateUserRequest>;
    to: To;
}

export default forwardRef<HTMLDivElement, ClientFormProps>(function ClientForm({ form, to, ...props }, ref) {
    const navigate = useNavigate();
    const [create, { isLoading: isCreating }] = useClientsCreateMutation();

    const {
        control,
        watch,
        handleSubmit,
        setError,
        reset,
        formState: { errors },
        clearErrors,
    } = form;

    const onSubmit: SubmitHandler<CreateUserRequest> = async form => {
        try {
            await create({
                createUserRequest: form,
            }).unwrap();

            reset();
            navigate(to);
        } catch (error) {
            const { data } = error as FetchBaseQueryError;
            const errorObject = processEmailError(data);

            setApiErrors(setError, errorObject);
        }
    };

    const [sendNotification] = watch(['send_notification']);

    const { t } = useTranslation();
    const labels = useFormLabels();

    return (
        <div {...props} ref={ref}>
            <form
                id={FormId}
                className={cn('fieldset-grid', 'fieldset-grid--columns', styles.grid)}
                onSubmit={handleSubmit(onSubmit)}
            >
                <FormField prefix={FormId} name="name" control={control} component={TextField} label={labels.name} />
                <FormField
                    prefix={FormId}
                    name="surname"
                    control={control}
                    component={TextField}
                    label={labels.surname}
                />
                <FormField prefix={FormId} name="phone" control={control} component={TextField} label={labels.phone} />
                <FormField
                    prefix={FormId}
                    name="email"
                    control={control}
                    component={TextField}
                    label={labels.email}
                    widgetProps={{
                        onChange: () => {
                            // @ts-ignore
                            clearErrors(NON_FIELD_ERROR_KEY);
                        },
                    }}
                />

                <NonFieldErrors className={cn('fieldset-grid-cell--full', styles.error)} errors={errors} />

                <FormField
                    control={control}
                    prefix={FormId}
                    name="send_notification"
                    component={Checkbox}
                    className="fieldset-grid-cell--full"
                    widgetProps={{
                        label: labels.send_notification,
                        defaultChecked: sendNotification,
                        disableIconOverlay: true,
                        disableProgrammaticRipple: true,
                        disableRipple: true,
                    }}
                />
            </form>

            <div className={styles.bottom}>
                <Divider className={cn('margin-top', 'rmd-divider-no-gutters')} />

                <SubmitButton form={FormId} prefix={_id()} isSending={isCreating}>
                    <FeatureContent contentKey={_id('submit')} fallback={t('buttons.nextStep', 'Next step')} />
                </SubmitButton>
            </div>
        </div>
    );
});
